import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  FiHome, FiFileText, FiLayers, FiShoppingCart, FiPackage,
  FiUsers, FiSettings, FiCreditCard, FiPieChart, FiTruck,
  FiBell, FiBarChart2, FiDatabase, FiCalendar, FiChevronDown, FiZap,
} from "react-icons/fi";
import axios from "axios";
import { baseurl } from "../baseurl";



const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

:root {
  --sb-bg:        #f0fafa;
  --sb-surface:   #ffffff;
  --sb-surface2:  #e8f5f5;
  --sb-border:    rgba(10,85,86,0.10);
  --sb-teal:      #0a5556;
  --sb-teal-mid:  #0c7172;
  --sb-teal-lite: #14b8a6;
  --sb-teal-faint:rgba(10,85,86,0.06);
  --sb-gold:      #FFD166;
  --sb-gold-dim:  rgba(255,209,102,0.22);
  --sb-text:      #2a4a4a;
  --sb-text-dim:  #7a9999;
  --sb-active-bg: rgba(10,85,86,0.09);
  --sb-hover-bg:  rgba(10,85,86,0.05);
  --sb-width:     260px;
  --sb-radius:    11px;
}

@keyframes sb__fadeIn   { from{opacity:0;transform:translateX(-8px)} to{opacity:1;transform:translateX(0)} }
@keyframes sb__pulse    { 0%,100%{box-shadow:0 0 0 0 rgba(10,85,86,0.28)} 70%{box-shadow:0 0 0 7px rgba(10,85,86,0)} }
@keyframes sb__floatA   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
@keyframes sb__floatB   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(8px)} }

/* ── SHELL ── */
.sb {
  position:fixed; top:0; left:0; bottom:0;
  width:var(--sb-width);
  background:var(--sb-bg);
  display:flex; flex-direction:column;
  z-index:1000;
  border-right:1px solid var(--sb-border);
  box-shadow:4px 0 28px rgba(10,85,86,0.08);
  overflow:hidden;
  font-family:'DM Sans',sans-serif;
  animation:sb__fadeIn 0.4s ease both;
}

/* gradient top accent */
.sb::before {
  content:'';
  position:absolute; top:0; left:0; right:0; height:3px;
  background:linear-gradient(90deg,var(--sb-teal) 0%,var(--sb-teal-lite) 55%,var(--sb-gold) 100%);
  z-index:2;
}

/* subtle grid texture */
.sb::after {
  content:'';
  position:absolute; inset:0; pointer-events:none;
  background-image:
    linear-gradient(rgba(10,85,86,0.018) 1px,transparent 1px),
    linear-gradient(90deg,rgba(10,85,86,0.018) 1px,transparent 1px);
  background-size:40px 40px;
  z-index:0;
}

/* ambient blobs */
.sb__glow {
  position:absolute; width:260px; height:260px;
  border-radius:50%; pointer-events:none; z-index:0;
  background:radial-gradient(circle,rgba(10,85,86,0.07) 0%,transparent 68%);
  bottom:-80px; left:-60px;
  animation:sb__floatB 14s ease-in-out infinite;
}
.sb__glow2 {
  position:absolute; width:180px; height:180px;
  border-radius:50%; pointer-events:none; z-index:0;
  background:radial-gradient(circle,rgba(20,184,166,0.06) 0%,transparent 70%);
  top:35%; right:-50px;
  animation:sb__floatA 11s ease-in-out infinite;
}

/* ── HEADER ── */
.sb__header {
  position:relative; z-index:1;
  padding:20px 18px 16px;
  border-bottom:1px solid var(--sb-border);
  flex-shrink:0;
  background:rgba(255,255,255,0.70);
  backdrop-filter:blur(14px);
}
.sb__brand { display:flex; align-items:center; gap:12px; }
.sb__brand-icon {
  width:42px; height:42px; border-radius:13px; flex-shrink:0;
  background:linear-gradient(135deg,var(--sb-teal) 0%,var(--sb-teal-lite) 100%);
  display:flex; align-items:center; justify-content:center;
  font-size:18px; color:#fff;
  box-shadow:0 6px 18px rgba(10,85,86,0.28);
  animation:sb__pulse 3s infinite;
}
.sb__brand-name {
  font-family:'Sora',sans-serif; font-weight:800;
  font-size:15px; color:var(--sb-teal);
  letter-spacing:-0.03em; line-height:1;
}
.sb__brand-tag {
  font-size:10px; color:var(--sb-teal-lite);
  font-weight:600; letter-spacing:0.08em;
  text-transform:uppercase; margin-top:3px;
}

/* ── NAV SCROLL ── */
.sb__nav {
  flex:1; overflow-y:auto; overflow-x:hidden;
  padding:10px 0 20px;
  position:relative; z-index:1;
  scrollbar-width:thin;
  scrollbar-color:rgba(10,85,86,0.14) transparent;
}
.sb__nav::-webkit-scrollbar { width:4px; }
.sb__nav::-webkit-scrollbar-track { background:transparent; }
.sb__nav::-webkit-scrollbar-thumb { background:rgba(10,85,86,0.14); border-radius:4px; }

/* section label */
.sb__section-label {
  font-size:9.5px; font-weight:700; letter-spacing:0.14em;
  text-transform:uppercase; color:var(--sb-text-dim);
  padding:14px 22px 6px;
  font-family:'Sora',sans-serif;
}

/* ── NAV ITEM WRAPPER ── */
.sb__item { padding:2px 10px; }

/* ── DIRECT LINK ── */
.sb__link {
  display:flex; align-items:center; gap:10px;
  padding:9.5px 13px; border-radius:var(--sb-radius);
  color:var(--sb-text); text-decoration:none;
  font-size:13.5px; font-weight:500;
  transition:background .18s,color .18s,transform .15s;
  position:relative; overflow:hidden; white-space:nowrap;
}
.sb__link:hover {
  background:var(--sb-hover-bg);
  color:var(--sb-teal);
  transform:translateX(3px);
  text-decoration:none;
}
.sb__link--active {
  background:var(--sb-active-bg) !important;
  color:var(--sb-teal) !important;
  font-weight:600;
}
.sb__link--active::before {
  content:'';
  position:absolute; left:0; top:18%; bottom:18%;
  width:3px; border-radius:0 3px 3px 0;
  background:linear-gradient(180deg,var(--sb-teal-lite),var(--sb-gold));
}

/* ── ICON TILE ── */
.sb__icon {
  width:31px; height:31px; border-radius:9px; flex-shrink:0;
  display:flex; align-items:center; justify-content:center;
  background:rgba(10,85,86,0.07);
  font-size:14px; color:var(--sb-teal);
  transition:background .18s,box-shadow .18s,color .18s;
}
.sb__link:hover .sb__icon,
.sb__link--active .sb__icon,
.sb__toggle:hover .sb__icon,
.sb__toggle--open .sb__icon {
  background:linear-gradient(135deg,var(--sb-teal) 0%,var(--sb-teal-lite) 100%);
  box-shadow:0 4px 12px rgba(10,85,86,0.22);
  color:#fff;
}

/* ── DROPDOWN TOGGLE ── */
.sb__toggle {
  display:flex; align-items:center; gap:10px;
  padding:9.5px 13px; border-radius:var(--sb-radius);
  color:var(--sb-text);
  background:transparent; border:none; width:100%;
  font-size:13.5px; font-weight:500;
  font-family:'DM Sans',sans-serif;
  cursor:pointer; text-align:left;
  transition:background .18s,color .18s;
  position:relative;
}
.sb__toggle:hover {
  background:var(--sb-hover-bg);
  color:var(--sb-teal);
}
.sb__toggle--open {
  color:var(--sb-teal);
  background:rgba(10,85,86,0.06);
}

.sb__label { flex:1; display:flex; align-items:center; gap:6px; }

.sb__arrow {
  font-size:14px; color:var(--sb-text-dim);
  transition:transform .25s,color .2s; flex-shrink:0;
}
.sb__arrow--open { transform:rotate(180deg); color:var(--sb-teal-lite); }

/* ── SUBMENU ── */
.sb__sub {
  overflow:hidden; max-height:0;
  transition:max-height .32s cubic-bezier(0.4,0,0.2,1),opacity .28s;
  opacity:0;
}
.sb__sub--open { max-height:500px; opacity:1; }
.sb__sub-inner {
  padding:4px 0 6px 52px;
  display:flex; flex-direction:column; gap:1px;
}
.sb__sub-link {
  display:flex; align-items:center; gap:8px;
  padding:7.5px 13px; border-radius:8px;
  color:var(--sb-text-dim); text-decoration:none;
  font-size:12.5px; font-weight:500;
  transition:background .15s,color .15s,transform .12s;
  position:relative;
}
.sb__sub-link::before {
  content:''; width:5px; height:5px; border-radius:50%;
  background:rgba(10,85,86,0.22); flex-shrink:0;
  transition:background .15s,box-shadow .15s;
}
.sb__sub-link:hover {
  background:var(--sb-hover-bg); color:var(--sb-teal);
  transform:translateX(3px); text-decoration:none;
}
.sb__sub-link:hover::before { background:var(--sb-teal-lite); }
.sb__sub-link--active {
  color:var(--sb-teal) !important;
  background:var(--sb-active-bg) !important;
  font-weight:600;
}
.sb__sub-link--active::before {
  background:var(--sb-teal-lite) !important;
  box-shadow:0 0 6px rgba(20,184,166,0.45);
}

/* ── SOON BADGE ── */
.sb__soon {
  font-size:9px; font-weight:700;
  font-family:'Sora',sans-serif;
  letter-spacing:0.06em; text-transform:uppercase;
  background:linear-gradient(135deg,rgba(255,209,102,0.22),rgba(255,209,102,0.12));
  border:1px solid rgba(255,209,102,0.38);
  color:#9a6400;
  padding:2px 7px; border-radius:50px;
  white-space:nowrap; flex-shrink:0;
}

/* ── FOOTER USER CARD ── */
.sb__footer {
  position:relative; z-index:1;
  padding:11px 13px;
  border-top:1px solid var(--sb-border);
  flex-shrink:0;
  background:rgba(255,255,255,0.65);
  backdrop-filter:blur(14px);
}
.sb__user {
  display:flex; align-items:center; gap:10px;
  padding:9px 11px; border-radius:var(--sb-radius);
  background:rgba(10,85,86,0.04);
  border:1.5px solid var(--sb-border);
  cursor:pointer; transition:background .18s,border-color .18s;
}
.sb__user:hover { background:rgba(10,85,86,0.07);border-color:rgba(10,85,86,0.18); }
.sb__user-avatar {
  width:34px; height:34px; border-radius:10px; flex-shrink:0;
  background:linear-gradient(135deg,var(--sb-teal),var(--sb-teal-lite));
  display:flex; align-items:center; justify-content:center;
  font-family:'Sora',sans-serif; font-weight:700;
  font-size:13px; color:#fff;
  box-shadow:0 4px 12px rgba(10,85,86,0.22);
}
.sb__user-info { flex:1; min-width:0; }
.sb__user-name {
  font-size:13px; font-weight:600; color:var(--sb-teal);
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
}
.sb__user-role { font-size:10.5px; color:var(--sb-text-dim); margin-top:1px; }
.sb__user-dot {
  width:8px; height:8px; border-radius:50%;
  background:#22c55e; flex-shrink:0;
  box-shadow:0 0 6px rgba(34,197,94,0.45);
}
`;

function Sidebar() {
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(null);
  // 1. New State for saved modules
  const [activeModules, setActiveModules] = useState([]);
  const token = localStorage.getItem("auth_token"); // Ensure you have access to the token

  const isActive = (path) => location.pathname === path;

  // 2. Fetch active modules on mount
  useEffect(() => {
    const fetchUserModules = async () => {
      try {
        const res = await axios.get(`${baseurl}getSavedBusinessModules`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success) {
          // Store just the names to make checking easy
          const names = res.data.data.map(m => m.name);
          setActiveModules(names);
        }
      } catch (error) {
        console.error("Sidebar modules fetch error:", error);
      }
    };
    if (token) fetchUserModules();
  }, [token]);

  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith("/invoice"))           setOpenMenu("invoice");
    else if (path.startsWith("/estimate"))    setOpenMenu("estimate");
    else if (path.startsWith("/purchase"))    setOpenMenu("purchase");
    else if (path.startsWith("/product"))     setOpenMenu("product");
    else if (path.startsWith("/inventory"))   setOpenMenu("inventory");
    else if (path.startsWith("/tax"))         setOpenMenu("tax");
    else if (path.startsWith("/expense"))     setOpenMenu("expense");
    else if (path.startsWith("/customers"))   setOpenMenu("customers");
    else if (path.startsWith("/suppliers"))   setOpenMenu("suppliers");
    else if (path.startsWith("/subscriptions")) setOpenMenu("subscriptions");
    else if (path.startsWith("/reports"))     setOpenMenu("reports");
    else if (path.startsWith("/notifications")) setOpenMenu("notifications");
    else if (path.startsWith("/settings"))    setOpenMenu("settings");
  }, [location]);

  const toggleMenu = (menu) => setOpenMenu(openMenu === menu ? null : menu);

  // Helper to check if a module is enabled
  const isEnabled = (moduleName) => activeModules.includes(moduleName);

  // Components (NavLink, NavDropdown, SubLink) remain exactly as you have them...
  const NavLink = ({ to, icon, label }) => (
    <div className="sb__item">
      <Link to={to} className={`sb__link ${isActive(to) ? "sb__link--active" : ""}`}>
        <div className="sb__icon">{icon}</div>
        <span className="sb__label">{label}</span>
      </Link>
    </div>
  );

  const NavDropdown = ({ id, icon, label, soon, children }) => {
    const isOpen = openMenu === id;
    return (
      <div className="sb__item">
        <button className={`sb__toggle ${isOpen ? "sb__toggle--open" : ""}`} onClick={() => toggleMenu(id)}>
          <div className="sb__icon">{icon}</div>
          <span className="sb__label">
            {label}
            {soon && <span className="sb__soon">Soon</span>}
          </span>
          {!soon && <FiChevronDown className={`sb__arrow ${isOpen ? "sb__arrow--open" : ""}`} />}
        </button>
        <div className={`sb__sub ${isOpen ? "sb__sub--open" : ""}`}>
          <div className="sb__sub-inner">{children}</div>
        </div>
      </div>
    );
  };

  const SubLink = ({ to, label }) => (
    <Link to={to} className={`sb__sub-link ${isActive(to) ? "sb__sub-link--active" : ""}`}>
      {label}
    </Link>
  );

  return (
    <>
      <style>{CSS}</style>
      <aside className="sb">
        <div className="sb__glow" />
        <div className="sb__glow2" />

        <div className="sb__header">
          <div className="sb__brand">
            <div className="sb__brand-icon"><FiZap /></div>
            <div className="sb__brand-text">
              <div className="sb__brand-name">Invoice ERP</div>
              <div className="sb__brand-tag">Accounting & Billing</div>
            </div>
          </div>
        </div>

        <nav className="sb__nav">
          <div className="sb__section-label">Main</div>
          {/* <NavLink to="/dashboard" icon={<FiHome size={15}/>} label="Dashboard" /> */}

          <div className="sb__section-label">Finance</div>

          {/* 3. Conditional Rendering based on Database names */}
          {isEnabled("Invoice & Billing") && (
            <NavDropdown id="invoice" icon={<FiFileText size={15}/>} label="Invoice & Billing">
              <SubLink to="/invoice-create" label="Create GST Invoice" />
              <SubLink to="/invoices"       label="All Invoices" />
              <SubLink to="/invoice-credit-notes" label="Credit Notes" />
            </NavDropdown>
          )}

          {isEnabled("Quotation / Estimates") && (
            <NavDropdown id="estimate" icon={<FiLayers size={15}/>} label="Quotations / Estimates">
              <SubLink to="/estimate-create" label="Create Estimate" />
              <SubLink to="/estimates"       label="All Estimates" />
            </NavDropdown>
          )}

          {isEnabled("Income & Expense Management") && (
            <NavDropdown id="expense" icon={<FiCreditCard size={15}/>} label="Income & Expense">
              <SubLink to="/expenses"            label="Expenses" />
              <SubLink to="/expense-add"         label="Add Expense" />
              <SubLink to="/expense-categories"  label="Expense Categories" />
              <SubLink to="/expense-report"      label="Expense Report" />
            </NavDropdown>
          )}

          {isEnabled("Payment Tracking") && (
            <div className="sb__item">
              <Link to="/payments" className={`sb__link ${isActive("/payments") ? "sb__link--active" : ""}`}>
                <div className="sb__icon"><FiCreditCard size={15}/></div>
                <span className="sb__label">
                  Payments
                  <span className="sb__soon">Soon</span>
                </span>
              </Link>
            </div>
          )}

          <div className="sb__section-label">Catalog</div>

          {isEnabled("Product Listing") && (
            <NavDropdown id="product" icon={<FiPackage size={15}/>} label="Products & Catalog">
              <SubLink to="/products"       label="Product List" />
              <SubLink to="/product-add"    label="Add Product" />
              <SubLink to="/product-catalog" label="Public Catalog" />
            </NavDropdown>
          )}

          {isEnabled("Stock & Inventory Management") && (
            <NavDropdown id="inventory" icon={<FiDatabase size={15}/>} label="Inventory">
              <SubLink to="/inventory-stock"   label="Current Stock" />
              <SubLink to="/inventory-adjust"  label="Stock Adjustment" />
              <SubLink to="/inventory-alerts"  label="Low Stock Alerts" />
            </NavDropdown>
          )}

          <div className="sb__section-label">CRM & Compliance</div>

          {isEnabled("Customer Management (CRM)") && (
            <NavDropdown id="customers" icon={<FiUsers size={15}/>} label="Customer CRM">
              <SubLink to="/customers"               label="Customer List" />
              <SubLink to="/customer-communication"  label="Communication Logs" />
            </NavDropdown>
          )}

          {isEnabled("GST Filing & Reports") && <NavDropdown id="tax" icon={<FiBarChart2 size={15}/>} label="GST Filing" soon />}
          {isEnabled("Subscription / Membership Billing") && <NavDropdown id="subscriptions" icon={<FiCalendar size={15}/>} label="Subscriptions" soon />}
          {isEnabled("Dashboard & Analytics") && <NavDropdown id="reports" icon={<FiPieChart size={15}/>} label="Reports & Analytics" soon />}

          <div className="sb__section-label">System</div>

          {isEnabled("Reminder & Notification System") && (
            <NavDropdown id="notifications" icon={<FiBell size={15}/>} label="Notifications">
              <SubLink to="/notifications-email"     label="Email Templates" />
              <SubLink to="/notifications-reminders" label="Payment Reminders" />
            </NavDropdown>
          )}

          <NavDropdown id="settings" icon={<FiSettings size={15}/>} label="Settings">
            <SubLink to="/settings-invoice-template" label="Invoice Template" />
          </NavDropdown>
        </nav>

        <div className="sb__footer">
          <div className="sb__user">
            <div className="sb__user-avatar">A</div>
            <div className="sb__user-info">
              <div className="sb__user-name">Admin User</div>
              <div className="sb__user-role">Super Admin</div>
            </div>
            <div className="sb__user-dot" />
          </div>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
